import React, { useEffect, useState, useRef } from "react";
import Layout, { PixelStyle } from "../../components/layout";
import { HeaderContainer } from "./viewreport";
import TableLayout, { Td, Tr } from "../../components/tableLayout";
import { GoDownload } from "react-icons/go";
import { IoAdd } from "react-icons/io5";
import { FiEdit3 } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdDone, MdOutlineClose } from "react-icons/md";
import "react-country-state-city/dist/react-country-state-city.css";

import { State } from "country-state-city";

import { useAppContext } from "../../context/appContext";
import { usePageLoader } from "../../hooks/usePageLoader";
import { usePagination } from "../../components/Pagination";
import { styled } from "@mui/material/styles";
import { TextField } from "@mui/material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ExportToExcel from "../../components/exportToExcel";
import { useQuery } from "@tanstack/react-query";
import { graphqlRequest } from "../../services/apiService";

const TableHead = [
  "Client name",
  "Industry",
  "Weekly hours",
  "Street address",
  "City",
  "State",
  "Zip code",
  "Action",
];
// console.log("12345  =>", State.getAllStates("IN"));
const ClientList = () => {
  const { UserType } = useAppContext();
  const [clients, setClients] = useState([]);
  const [stateList, setStateList] = useState(State.getStatesOfCountry("US"));
  // const [country, setCountry] = useState(null);
  const [currentState, setCurrentState] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editedClient, setEditedClient] = useState(null);
  const [sortedReports, setSortedReports] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filters, setFilters] = useState({
    clientName: "",
    address: "",
    city: "",
    state: "",
    weeklyHours: "",
    industry: "",
    zipCode: "",
  });

  const inputRefs = useRef({});
  
  // Use intelligent loading hook
  const { startLoading, stopLoading, setLoadingError } = usePageLoader({
    initialLoading: true,
    staleTime: 3 * 60 * 1000, // 3 minutes cache for client list
    loadingText: "Loading client list...",
  });

  // Pagination hook
  const {
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(sortedReports, 50);

  const headerFieldMapping = {
    "Client Name": "clientName",
    "Street Address": "address",
    City: "city",
    State: "state",
    Industry: "industry",
    "Weekly Hours": "weeklyHours",
    "Zip Code": "zipCode",
  };

  const fetchCompanyList = async (token) => {
    try {
      const query = `
        query GetClientList {
          getClientList {
            id
            company
            city
            zipCode
            address
            state
            weeklyHours
            industry
            clientName
            clientType
            streetAddress
          }
        }
      `;

      const data = await graphqlRequest(query, {});
      return data.getClientList;
    } catch (error) {
      console.log(error.message || "Error fetching data");
      setLoadingError(error);
      throw error;
    }
  };

  const deleteSingleCompany = async (id) => {
    try {
      const query = `
        mutation DeleteSingleClient($deleteSingleClientId: ID!) {
          deleteSingleClient(id: $deleteSingleClientId)
        }
      `;

      await graphqlRequest(query, { deleteSingleClientId: id });
      setClients(clients.filter((client) => client.id !== id));
    } catch (error) {
      throw new Error(error.message || "Failed to delete");
    }
  };

  // const { data, isLoading } = useQuery({
  //   queryKey: ["companyList"],
  //   queryFn: () => fetchCompanyList(UserType?.token),
  //   staleTime: 5 * 60 * 1000,
  //   retry: 2,
  // });

  useEffect(() => {
    const loadClients = async () => {
      startLoading();
      try {
        const data = await fetchCompanyList();
        setClients(data);
      } catch (error) {
        console.error("Failed to load clients:", error);
        setLoadingError(error);
      } finally {
        stopLoading();
      }
    };
    
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // useEffect(() => {
  //   // GetState("US").then((result) => console.log("stateList", result));
  // }, []);

  // console.log("stateList", country, currentState);

  const _addClient = () => {
    const newClient = {
      id: Date.now(),
      clientName: "",
      address: "",
      city: "",
      state: "",
      industry: "",
      weeklyHours: "",
      zipCode: "",
      isNew: true,
    };
    // Always add the new client to the array, regardless of current length
    setClients([...clients, newClient]);
    setEditingId(newClient.id);
    setEditedClient({ ...newClient });
  };

  const startEditing = (clientId) => {
    const clientToEdit =
      clients && clients.find((client) => client.id === clientId);
    setEditingId(clientId);
    setEditedClient({ ...clientToEdit });
  };

  const handleInputChange = (field, value) => {
    setEditedClient((prev) => ({ ...prev, [field]: value }));
    setTimeout(() => {
      const input = inputRefs.current[`${editingId}-${field}`];
      if (input) input.focus();
    }, 0);
  };

  const _createOrUpdateLocation = async (user) => {
    const variables = {
      createAndUpdateClientId: user.id && !user.isNew ? String(user.id) : null,
      company: user.company || "",
      zipCode: user.zipCode || "",
      city: user.city || "",
      address: user.address || "",
      industry: user.industry || "",
      state: user.state || "",
      weeklyHours: user.weeklyHours || "",
      clientName: user.clientName || "",
      clientType: user.clientType || "",
      streetAddress: user.address || "",
    };

    try {
      const query = `
        mutation CreateAndUpdateClient(
          $company: String
          $zipCode: String
          $city: String
          $address: String
          $industry: String
          $weeklyHours: String
          $state: String
          $clientName: String
          $clientType: String
          $streetAddress: String
          $createAndUpdateClientId: String
        ) {
          createAndUpdateClient(
            company: $company
            zipCode: $zipCode
            city: $city
            address: $address
            industry: $industry
            weeklyHours: $weeklyHours
            state: $state
            clientName: $clientName
            clientType: $clientType
            streetAddress: $streetAddress
            id: $createAndUpdateClientId
          ) {
            company city zipCode address state industry weeklyHours clientName clientType streetAddress id
          }
        }
      `;

      const result = await graphqlRequest(query, variables);
      if (result?.createAndUpdateClient) {
        const updatedClient = result.createAndUpdateClient;
        setClients(
          clients.map((c) =>
            c.id === user.id ? { ...updatedClient, isNew: false } : c
          )
        );
        setEditingId(null);
        setEditedClient(null);
      }
    } catch (error) {
      console.error("Error creating/updating client:", error);
      throw error;
    }
  };

  const _deleteClientLocation = async (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      await deleteSingleCompany(id);
    }
  };

  const cancelEdit = (clientId) => {
    setEditingId(null);
    setEditedClient(null);
    setClients(clients.filter((c) => !c.isNew || c.id !== clientId));
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  // const toggleSortOrder = (header) => {
  //   const field = headerFieldMapping[header];
  //   if (field) {
  //     setSortField(field);
  //     setSortOrder(
  //       sortField === field
  //         ? sortOrder === "asc"
  //           ? "desc"
  //           : sortOrder === "desc"
  //           ? null
  //           : "asc"
  //         : "asc"
  //     );
  //   }
  // };

  // useEffect(() => {
  //   const filteredReports = clients.filter((client) =>
  //     Object.entries(filters).every(
  //       ([key, value]) =>
  //         !value || client[key]?.toLowerCase().includes(value.toLowerCase())
  //     )
  //   );

  //   setSortedReports(
  //     sortField
  //       ? [...filteredReports].sort((a, b) => {
  //           const aValue = a[sortField]?.toLowerCase() || "";
  //           const bValue = b[sortField]?.toLowerCase() || "";
  //           return sortOrder === "asc"
  //             ? aValue.localeCompare(bValue)
  //             : bValue.localeCompare(aValue);
  //         })
  //       : filteredReports
  //   );
  // }, [clients, filters, sortField, sortOrder]);

  // const headerFieldMapping = {
  //   "Client Name": "clientName",
  //   "Street Address": "address",
  //   City: "city",
  //   State: "state",
  //   Industry: "industry",
  //   "Weekly Hours": "weeklyHours",
  //   "Zip Code": "zipCode",
  // };

  // Add state to track the sort cycle
  // const [sortField, setSortField] = useState(null);
  // const [sortOrder, setSortOrder] = useState("asc");
  const [sortCycle, setSortCycle] = useState({}); // Tracks clicks per header

  const toggleSortOrder = (header) => {
    const field = headerFieldMapping[header];
    if (field) {
      setSortCycle((prev) => {
        const currentCycle = prev[header] || 0;
        const nextCycle = (currentCycle + 1) % 3; // Cycle: 0 (asc), 1 (desc), 2 (none)

        if (nextCycle === 0) {
          setSortField(field);
          setSortOrder("asc");
        } else if (nextCycle === 1) {
          setSortField(field);
          setSortOrder("desc");
        } else {
          setSortField(null); // Reset to original order
          setSortOrder("asc"); // Reset to default
        }

        return { ...prev, [header]: nextCycle };
      });
    }
  };

  useEffect(() => {
    const filteredReports =
      clients &&
      clients.filter((client) =>
        Object.entries(filters).every(
          ([key, value]) =>
            !value || client[key]?.toLowerCase().includes(value.toLowerCase())
        )
      );

    if (sortField) {
      setSortedReports(
        [...filteredReports].sort((a, b) => {
          if (sortField === "weeklyHours") {
            const aValue = Number(a[sortField]) || 0;
            const bValue = Number(b[sortField]) || 0;
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
          }
          const aValue = a[sortField]?.toLowerCase() || "";
          const bValue = b[sortField]?.toLowerCase() || "";
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        })
      );
    } else {
      setSortedReports(filteredReports); // Original unsorted order
    }
  }, [clients, filters, sortField, sortOrder]);

  // useEffect(() => {
  //   const filteredReports = clients.filter((client) =>
  //     Object.entries(filters).every(
  //       ([key, value]) =>
  //         !value || client[key]?.toLowerCase().includes(value.toLowerCase())
  //     )
  //   );

  //   if (sortField) {
  //     setSortedReports(
  //       [...filteredReports].sort((a, b) => {
  //         if (sortField === "weeklyHours") {
  //           const aValue = Number(a[sortField]) || 0;
  //           const bValue = Number(b[sortField]) || 0;
  //           return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
  //         }
  //         const aValue = a[sortField]?.toLowerCase() || "";
  //         const bValue = b[sortField]?.toLowerCase() || "";
  //         return sortOrder === "asc"
  //           ? aValue.localeCompare(bValue)
  //           : bValue.localeCompare(aValue);
  //       })
  //     );
  //   } else {
  //     setSortedReports(filteredReports);
  //   }
  // }, [clients, filters, sortField, sortOrder]);

  const ExportPayload = (data) => {
    return (Array.isArray(data) ? data : [data]).map(
      ({ id, createdAt, ...rest }) => ({
        "Client Name": rest.clientName,
        Industry: rest.industry,
        weeklyHours: rest.weeklyHours,
        Address: rest.address,
        City: rest.city,
        State: rest.state,
        "Zip Code": rest.zipCode,
      })
    );
  };

  const CustomTextField = styled(TextField)({
    "& .MuiOutlinedInput-root": {
      "& input": { padding: "5px 10px" },
      "& fieldset": { border: "0.5px solid rgb(87 83 78)" },
    },
    width: "100%",
    borderRadius: "5px",
  });

  // Show loading state if needed - now handled by global loader
  // The global loader will show automatically when isLoading is true

  return (
    <div className={`w-full text-sm h-full ${PixelStyle}`}>
      <HeaderContainer pageTitle="client list" isRedirecting={false}>
        <span className="text-black">client list</span>
      </HeaderContainer>

      <div className="border bg-white rounded">
        <div className="flex justify-between items-center px-5 py-2">
          <h4 className="text-2xl font-semibold">List</h4>
          <div className="flex items-center gap-4">
            <button
              className="text-zinc-500 p-2 rounded flex items-center gap-2 hover:bg-zinc-100 border"
              onClick={() =>
                ExportToExcel(ExportPayload(clients), "Client list")
              }
            >
              <GoDownload className="text-xl" />
            </button>
            <button
              onClick={_addClient}
              className="hover:bg-blue-100 text-blue-600 hover:font-medium border border-blue-600 p-1 px-3 rounded-md flex items-center gap-1"
            >
              <IoAdd className="text-xl" />
              <span>Add</span>
            </button>
          </div>
        </div>

        <TableLayout
          TableHead={TableHead}
          sortField={sortField}
          sortOrder={sortOrder}
          toggleSortOrder={toggleSortOrder}
          headerFieldMapping={headerFieldMapping}
          maxHeight="70vh"
          enableTableScroll={true}
          showPagination={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          <>
            <tr className="border-b bg-zinc-50">
              {TableHead.slice(0, -1).map(
                (header, index) => (
                  // header !== "Weekly Client Hours" ? (
                  <th key={index} className="px-6 py-1">
                    <input
                      placeholder={`Filter by ${header}`}
                      name={headerFieldMapping[header]}
                      value={filters[headerFieldMapping[header]]}
                      onChange={handleFilterChange}
                      className="border font-normal p-1 px-2 rounded-md outline-blue-500"
                    />
                  </th>
                )
                // ) : (
                //   <th key={index}></th>
                // )
              )}
              <th></th>
            </tr>

            {paginatedData && paginatedData.length > 0 ? (
              paginatedData.map((user) => {
                const isEditing = editingId === user.id;
                const displayClient = isEditing ? editedClient : user;

                return (
                  <Tr key={user.id} className={isEditing ? "bg-zinc-300" : ""}>
                    <Td>
                      {isEditing ? (
                        <CustomTextField
                          value={displayClient?.clientName || ""}
                          onChange={(e) =>
                            handleInputChange("clientName", e.target.value)
                          }
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-clientName`] = el)
                          }
                        />
                      ) : (
                        user.clientName || ""
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <CustomTextField
                          value={displayClient?.industry || ""}
                          onChange={(e) =>
                            handleInputChange("industry", e.target.value)
                          }
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-industry`] = el)
                          }
                        />
                      ) : (
                        user.industry || ""
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <CustomTextField
                          value={displayClient?.weeklyHours || ""}
                          onChange={(e) =>
                            handleInputChange("weeklyHours", e.target.value)
                          }
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-weeklyHours`] = el)
                          }
                        />
                      ) : (
                        user.weeklyHours || ""
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <CustomTextField
                          value={displayClient?.address || ""}
                          onChange={(e) =>
                            handleInputChange("address", e.target.value)
                          }
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-address`] = el)
                          }
                        />
                      ) : (
                        user.address || ""
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <CustomTextField
                          value={displayClient?.city || ""}
                          onChange={(e) =>
                            handleInputChange("city", e.target.value)
                          }
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-city`] = el)
                          }
                        />
                      ) : (
                        user.city || ""
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <Select
                          value={displayClient?.state || ""}
                          style={{
                            width: "100%",
                            border: "1px solid #00000090",
                            paddingLeft: "5px",
                            borderRadius: "4px",
                          }}
                          onChange={(e) =>
                            handleInputChange("state", e.target.value)
                          }
                          variant="standard"
                          disableUnderline
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-state`] = el)
                          }
                        >
                          {stateList.map((item) => (
                            <MenuItem key={item.isoCode} value={item.name}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        user.state || ""
                      )}
                    </Td>
                    <Td>
                      {isEditing ? (
                        <CustomTextField
                          value={displayClient?.zipCode || ""}
                          onChange={(e) =>
                            handleInputChange("zipCode", e.target.value)
                          }
                          inputRef={(el) =>
                            (inputRefs.current[`${user.id}-zipCode`] = el)
                          }
                        />
                      ) : (
                        user.zipCode || ""
                      )}
                    </Td>
                    <Td className="text-center flex gap-3">
                      {isEditing ? (
                        <>
                          <button
                            className="hover:text-green-500 hover:bg-green-100 p-1.5 rounded-lg text-lg"
                            onClick={() =>
                              _createOrUpdateLocation(editedClient)
                            }
                          >
                            <MdDone />
                          </button>
                          <button
                            className="hover:text-red-500 hover:bg-red-100 p-1.5 rounded-lg text-lg"
                            onClick={() => cancelEdit(user.id)}
                          >
                            <MdOutlineClose />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="hover:text-blue-500 hover:bg-blue-100 p-1.5 rounded-lg text-lg"
                            onClick={() => startEditing(user.id)}
                          >
                            <FiEdit3 />
                          </button>
                          <button
                            className="hover:text-red-500 hover:bg-red-100 p-1.5 rounded-lg text-lg"
                            onClick={() => _deleteClientLocation(user.id)}
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </>
                      )}
                    </Td>
                  </Tr>
                );
              })
            ) : (
              <Tr>
                <Td colSpan={TableHead.length}>
                  {/* {isLoading ? "Loading Client List..." : "No Clients Found"} */}
                </Td>
              </Tr>
            )}
          </>
        </TableLayout>
      </div>
    </div>
  );
};

export default ClientList;
